import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Mail, Phone, Calendar, Users, DollarSign, MapPin, MessageSquare, Trash2, RefreshCw, Plus, Edit2, Save, X, Image, FileText, Globe, Settings, Home, Compass, ChevronDown, ChevronUp, Upload, Video, Star, Quote, Briefcase, Download, Clock, CheckCircle, XCircle, PhoneCall, UserCheck, TrendingUp, Send, Activity, AlertTriangle, Shield, Wrench, Database, HardDrive, Zap, User, Eye, EyeOff, Lock, Cake, Heart, CreditCard, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

// Import extracted components
import { QuickActionsPanel } from '../components/admin/DashboardHome';
import { UserManagementPanel } from '../components/admin/UsersManager';
import { MediaUpload, ImageUpload, ExperienceForm, TestimonialForm, GalleryItemForm } from '../components/admin/GalleryManager';
import { StorageCapacityPanel, FormFieldEditor, AdminProfilePanel, SettingsPanel } from '../components/admin/SettingsManager';
import { HealthCheckPanel } from '../components/admin/HealthManager';
import AdminErrorBoundary from '../components/admin/AdminErrorBoundary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-profile');
  
  // User and permissions state
  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState({
    can_manage_users: false,
    can_manage_settings: false,
    can_manage_content: false,
    can_manage_leads: false,
    can_export_data: false,
    can_view_dashboard: true
  });
  
  // Data states
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [travelGallery, setTravelGallery] = useState([]);
  const [heroContent, setHeroContent] = useState(null);
  const [philosophyContent, setPhilosophyContent] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  
  // Itinerary Leads states
  const [itineraryLeads, setItineraryLeads] = useState([]);
  const [itineraryStats, setItineraryStats] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [leadsFilter, setLeadsFilter] = useState('all');
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Design Journey CMS states
  const [designJourneyContent, setDesignJourneyContent] = useState(null);
  const [editingDesignJourney, setEditingDesignJourney] = useState(false);
  
  // Edit states
  const [editingExperience, setEditingExperience] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [editingHero, setEditingHero] = useState(false);
  const [editingPhilosophy, setEditingPhilosophy] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  
  // Homepage Settings states
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [editingHomepageSettings, setEditingHomepageSettings] = useState(false);
  
  // Form states
  const [newExperience, setNewExperience] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState(null);
  const [newGalleryItem, setNewGalleryItem] = useState(null);

  // Blog states
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [newBlogPost, setNewBlogPost] = useState(null);
  const [blogPostFilter, setBlogPostFilter] = useState('all');

  // Countries states
  const [countries, setCountries] = useState([]);
  const [editingCountry, setEditingCountry] = useState(null);
  const [newCountry, setNewCountry] = useState(null);
  const [countryFilter, setCountryFilter] = useState('all');
  
  // JNT Tales sub-tab (countries or cities)
  const [jntTalesSubTab, setJntTalesSubTab] = useState('countries');

  // Expanded sections
  const [expandedInquiry, setExpandedInquiry] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (!token && !isLoggingOut) {
      navigate('/admin/login');
      return;
    }
    if (token) {
      fetchUserInfo();
      fetchAllData();
    }
  }, [token, navigate, isLoggingOut]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
      setPermissions(response.data.permissions || {
        can_manage_users: false,
        can_manage_settings: false,
        can_manage_content: false,
        can_manage_leads: false,
        can_export_data: false,
        can_view_dashboard: true
      });
    } catch (error) {
      console.error('Error fetching user info:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, inquiriesRes, experiencesRes, testimonialsRes, galleryRes, heroRes, philosophyRes, contactRes, leadsRes, leadsStatsRes, designJourneyRes, blogCategoriesRes, blogPostsRes, countriesRes, homepageSettingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/experiences`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/testimonials`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/travel-gallery`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/content/hero`).catch(() => ({ data: null })),
        axios.get(`${API}/content/philosophy`).catch(() => ({ data: null })),
        axios.get(`${API}/content/contact`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/itinerary-leads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/itinerary-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/content/design-journey`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/blog/categories`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/blog/posts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/countries`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/content/homepage-settings`).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);
      setExperiences(experiencesRes.data);
      setTestimonials(testimonialsRes.data);
      setTravelGallery(galleryRes.data || []);
      setHeroContent(heroRes.data);
      setPhilosophyContent(philosophyRes.data);
      setContactInfo(contactRes.data);
      setItineraryLeads(leadsRes.data || []);
      setItineraryStats(leadsStatsRes.data);
      setDesignJourneyContent(designJourneyRes.data);
      setBlogCategories(blogCategoriesRes.data || []);
      setBlogPosts(blogPostsRes.data || []);
      setCountries(countriesRes.data || []);
      setHomepageSettings(homepageSettingsRes?.data || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggingOut(true);
    localStorage.removeItem('admin_token');
    setToken(null);
    navigate('/');
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/inquiries/${inquiryId}`, { status: newStatus }, { headers });
      setInquiries(prev => prev.map(inq => 
        inq.id === inquiryId ? { ...inq, status: newStatus } : inq
      ));
    } catch (error) {
      console.error('Error updating inquiry:', error);
      alert('Failed to update inquiry status');
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/inquiries/${inquiryId}`, { headers });
      setInquiries(prev => prev.filter(inq => inq.id !== inquiryId));
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      alert('Failed to delete inquiry');
    }
  };

  const seedData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/seed-data`, {}, { headers });
      alert(response.data.message);
      fetchAllData();
    } catch (error) {
      console.error('Error seeding data:', error);
      alert('Failed to seed data: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Itinerary Leads Functions
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/itinerary-leads/${leadId}/status?status=${newStatus}`, {}, { headers });
      setItineraryLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Failed to update status');
    }
  };

  const addNoteToLead = async (leadId) => {
    if (!newNote.trim()) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/itinerary-leads/${leadId}/notes`, { note: newNote }, { headers });
      const updatedLeads = itineraryLeads.map(lead => {
        if (lead.id === leadId) {
          return { ...lead, notes: [...(lead.notes || []), response.data.note] };
        }
        return lead;
      });
      setItineraryLeads(updatedLeads);
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, notes: [...(selectedLead.notes || []), response.data.note] });
      }
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    }
  };

  const deleteNote = async (leadId, noteId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/itinerary-leads/${leadId}/notes/${noteId}`, { headers });
      const updatedLeads = itineraryLeads.map(lead => {
        if (lead.id === leadId) {
          return { ...lead, notes: (lead.notes || []).filter(n => n.id !== noteId) };
        }
        return lead;
      });
      setItineraryLeads(updatedLeads);
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, notes: (selectedLead.notes || []).filter(n => n.id !== noteId) });
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/itinerary-leads/${leadId}`, { headers });
      setItineraryLeads(prev => prev.filter(lead => lead.id !== leadId));
      if (selectedLead?.id === leadId) {
        setSelectedLead(null);
      }
      // Refresh stats after deletion
      fetchAllData();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead');
    }
  };

  // Toggle select for bulk delete
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Select all visible leads
  const selectAllLeads = () => {
    const filteredIds = filteredLeads.map(lead => lead.id);
    if (selectedLeadIds.length === filteredIds.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(filteredIds);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedLeadIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedLeadIds.length} leads? This action cannot be undone.`)) return;
    
    setBulkDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/admin/itinerary-leads/bulk-delete`, selectedLeadIds, { headers });
      
      if (response.data.success) {
        setItineraryLeads(prev => prev.filter(lead => !selectedLeadIds.includes(lead.id)));
        setSelectedLeadIds([]);
        if (selectedLead && selectedLeadIds.includes(selectedLead.id)) {
          setSelectedLead(null);
        }
        // Refresh stats after bulk deletion
        fetchAllData();
        alert(`Successfully deleted ${response.data.deleted_count} leads`);
      }
    } catch (error) {
      console.error('Error bulk deleting leads:', error);
      alert('Failed to delete leads');
    } finally {
      setBulkDeleting(false);
    }
  };


  const getStatusColor = (status) => {
    const colors = {
      'pending_verification': '#f6ad55',
      'verified': '#68d391',
      'generating': '#63b3ed',
      'completed': '#48bb78',
      'callback_requested': '#fc8181',
      'contacted': '#b794f4',
      'converted': '#38a169',
      'lost': '#e53e3e'
    };
    return colors[status] || '#a0aec0';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending_verification': 'Pending',
      'verified': 'Verified',
      'generating': 'Generating',
      'completed': 'Completed',
      'callback_requested': 'Callback',
      'contacted': 'Contacted',
      'converted': 'Converted',
      'lost': 'Lost'
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filteredLeads = itineraryLeads.filter(lead => {
    if (leadsFilter === 'all') return true;
    return lead.status === leadsFilter;
  });

  // Experience CRUD
  const saveExperience = async (experience) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (experience.id && !newExperience) {
        await axios.put(`${API}/admin/experiences/${experience.id}`, experience, { headers });
      } else {
        await axios.post(`${API}/admin/experiences`, experience, { headers });
      }
      setEditingExperience(null);
      setNewExperience(null);
      fetchAllData();
    } catch (error) {
      console.error('Error saving experience:', error);
      alert('Failed to save experience');
    }
  };

  const deleteExperience = async (experienceId) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/experiences/${experienceId}`, { headers });
      fetchAllData();
    } catch (error) {
      console.error('Error deleting experience:', error);
      alert('Failed to delete experience');
    }
  };

  const duplicateExperience = async (experience) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const duplicate = { ...experience, title: `${experience.title} (Copy)`, id: undefined };
      delete duplicate.id;
      delete duplicate._id;
      await axios.post(`${API}/admin/experiences`, duplicate, { headers });
      fetchAllData();
    } catch (error) {
      console.error('Error duplicating experience:', error);
      alert('Failed to duplicate experience');
    }
  };

  // Testimonial CRUD
  const saveTestimonial = async (testimonial) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (testimonial.id && !newTestimonial) {
        await axios.put(`${API}/admin/testimonials/${testimonial.id}`, testimonial, { headers });
      } else {
        await axios.post(`${API}/admin/testimonials`, testimonial, { headers });
      }
      setEditingTestimonial(null);
      setNewTestimonial(null);
      fetchAllData();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Failed to save testimonial');
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/testimonials/${testimonialId}`, { headers });
      fetchAllData();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      alert('Failed to delete testimonial');
    }
  };

  // Content Updates
  const saveHeroContent = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/content/hero`, heroContent, { headers });
      setEditingHero(false);
      alert('Hero content saved successfully');
    } catch (error) {
      console.error('Error saving hero:', error);
      alert('Failed to save hero content');
    }
  };

  const savePhilosophyContent = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/content/philosophy`, philosophyContent, { headers });
      setEditingPhilosophy(false);
      alert('Philosophy content saved successfully');
    } catch (error) {
      console.error('Error saving philosophy:', error);
      alert('Failed to save philosophy content');
    }
  };

  const saveContactInfo = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/content/contact`, contactInfo, { headers });
      setEditingContact(false);
      alert('Contact info saved successfully');
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact info');
    }
  };

  // Homepage Settings save function
  const saveHomepageSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/content/homepage-settings`, homepageSettings, { headers });
      setEditingHomepageSettings(false);
      alert('Homepage settings saved successfully');
    } catch (error) {
      console.error('Error saving homepage settings:', error);
      alert('Failed to save homepage settings');
    }
  };

  // Travel Gallery CRUD functions
  const saveGalleryItem = async (item) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (item.id && !newGalleryItem) {
        // Update existing
        await axios.put(`${API}/admin/travel-gallery/${item.id}`, item, { headers });
        setTravelGallery(travelGallery.map(g => g.id === item.id ? item : g));
      } else {
        // Create new
        const response = await axios.post(`${API}/admin/travel-gallery`, item, { headers });
        setTravelGallery([...travelGallery, response.data]);
      }
      setEditingGalleryItem(null);
      setNewGalleryItem(null);
      alert('Gallery item saved successfully');
    } catch (error) {
      console.error('Error saving gallery item:', error);
      alert('Failed to save gallery item');
    }
  };

  const deleteGalleryItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this gallery item?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/travel-gallery/${itemId}`, { headers });
      setTravelGallery(travelGallery.filter(g => g.id !== itemId));
      alert('Gallery item deleted successfully');
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      alert('Failed to delete gallery item');
    }
  };

  // Blog Post Functions
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const saveBlogPost = async (post) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Only send fields that match the backend schema
      const postData = {
        title: post.title,
        slug: post.slug || generateSlug(post.title),
        country_id: post.country_id,
        continent: post.continent,
        featured_image: post.featured_image || null,
        image_alt: post.image_alt || null,
        introduction: post.introduction,
        content: post.content || null,
        best_time: post.best_time || null,
        insider_tip: post.insider_tip || null,
        jnt_experience: post.jnt_experience || null,
        hidden_gems: post.hidden_gems || null,
        getting_around: post.getting_around || null,
        best_cafes: post.best_cafes || null,
        best_restaurants: post.best_restaurants || null,
        best_hotels: post.best_hotels || null,
        photo_spots: post.photo_spots || null,
        curated_experiences: post.curated_experiences || null,
        rail_cruise_tours: post.rail_cruise_tours || null,
        indian_traveler_guide: post.indian_traveler_guide || null,
        seo_keywords: post.seo_keywords || null,
        long_tail_keywords: post.long_tail_keywords || null,
        meta_title: post.meta_title || null,
        meta_description: post.meta_description || null,
        keywords: post.keywords || null,
        is_featured: post.is_featured || false,
        order: post.order || 0,
        status: post.status || 'published'
      };

      if (post.id) {
        await axios.put(`${API}/admin/blog/posts/${post.id}`, postData, { headers });
        setBlogPosts(blogPosts.map(p => p.id === post.id ? { ...p, ...postData } : p));
      } else {
        const res = await axios.post(`${API}/admin/blog/posts`, postData, { headers });
        setBlogPosts([...blogPosts, res.data]);
      }
      setEditingBlogPost(null);
      setNewBlogPost(null);
      alert('Blog post saved successfully');
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert('Failed to save blog post: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/blog/posts/${postId}`, { headers });
      setBlogPosts(blogPosts.filter(p => p.id !== postId));
      alert('Blog post deleted successfully');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post');
    }
  };

  const getFilteredBlogPosts = () => {
    if (blogPostFilter === 'all') return blogPosts;
    return blogPosts.filter(p => p.continent?.toLowerCase().replace(/\s+/g, '-') === blogPostFilter);
  };

  // Country Management Functions
  const saveCountry = async (countryData) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const slug = countryData.slug || countryData.name.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '');

      if (countryData.id) {
        await axios.put(`${API}/admin/countries/${countryData.id}`, countryData, { headers });
        setCountries(countries.map(c => c.id === countryData.id ? { ...c, ...countryData } : c));
      } else {
        const res = await axios.post(`${API}/admin/countries`, { ...countryData, slug }, { headers });
        setCountries([...countries, res.data]);
      }
      setEditingCountry(null);
      setNewCountry(null);
      alert('Country saved successfully');
    } catch (error) {
      console.error('Error saving country:', error);
      alert('Failed to save country');
    }
  };

  const deleteCountry = async (countryId) => {
    if (!window.confirm('Are you sure you want to delete this country? All cities under it must be deleted first.')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/countries/${countryId}`, { headers });
      setCountries(countries.filter(c => c.id !== countryId));
      alert('Country deleted successfully');
    } catch (error) {
      console.error('Error deleting country:', error);
      alert(error.response?.data?.detail || 'Failed to delete country');
    }
  };

  const duplicateCountry = async (country) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const duplicate = { ...country, name: `${country.name} (Copy)`, slug: `${country.slug}-copy` };
      delete duplicate.id;
      delete duplicate._id;
      await axios.post(`${API}/admin/countries`, duplicate, { headers });
      fetchAllData();
    } catch (error) {
      console.error('Error duplicating country:', error);
      alert('Failed to duplicate country');
    }
  };

  const duplicateBlogPost = async (post) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const duplicate = {
        title: `${post.title} (Copy)`,
        slug: `${post.slug}-copy`,
        country_id: post.country_id,
        continent: post.continent,
        featured_image: post.featured_image || null,
        image_alt: post.image_alt || null,
        introduction: post.introduction,
        content: post.content || null,
        best_time: post.best_time || null,
        insider_tip: post.insider_tip || null,
        jnt_experience: post.jnt_experience || null,
        hidden_gems: post.hidden_gems || null,
        getting_around: post.getting_around || null,
        best_cafes: post.best_cafes || null,
        best_restaurants: post.best_restaurants || null,
        best_hotels: post.best_hotels || null,
        photo_spots: post.photo_spots || null,
        curated_experiences: post.curated_experiences || null,
        rail_cruise_tours: post.rail_cruise_tours || null,
        indian_traveler_guide: post.indian_traveler_guide || null,
        seo_keywords: post.seo_keywords || null,
        long_tail_keywords: post.long_tail_keywords || null,
        meta_title: post.meta_title || null,
        meta_description: post.meta_description || null,
        keywords: post.keywords || null,
        is_featured: false,
        order: post.order || 0,
        status: 'draft'
      };
      await axios.post(`${API}/admin/blog/posts`, duplicate, { headers });
      fetchAllData();
    } catch (error) {
      console.error('Error duplicating blog post:', error);
      alert('Failed to duplicate blog post: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getFilteredCountries = () => {
    if (countryFilter === 'all') return countries;
    return countries.filter(c => c.continent?.toLowerCase().replace(/\s+/g, '-') === countryFilter);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  // Filter tabs based on permissions
  const allTabs = [
    { id: 'my-profile', label: 'My Profile', icon: User, permission: 'can_view_dashboard' },
    { id: 'overview', label: 'Overview', icon: Home, permission: 'can_view_dashboard' },
    { id: 'leads', label: 'Itinerary Leads', icon: Briefcase, badge: stats?.new_leads || 0, permission: 'can_manage_leads' },
    { id: 'inquiries', label: 'Inquiries', icon: MessageSquare, badge: stats?.new_inquiries || 0, permission: 'can_manage_leads' },
    { id: 'experiences', label: 'Experiences', icon: Compass, permission: 'can_manage_content' },
    { id: 'design-journey', label: 'Design Journey', icon: Globe, permission: 'can_manage_content' },
    { id: 'testimonials', label: 'Testimonials', icon: Quote, permission: 'can_manage_content' },
    { id: 'travel-gallery', label: 'Travel Gallery', icon: Image, permission: 'can_manage_content' },
    { id: 'jnt-tales', label: 'JNT Tales', icon: Globe, permission: 'can_manage_content' },
    { id: 'content', label: 'Site Content', icon: FileText, permission: 'can_manage_content' },
    { id: 'settings', label: 'Settings', icon: Settings, permission: 'can_manage_settings' }
  ];

  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => {
    // Everyone can view dashboard
    if (tab.permission === 'can_view_dashboard') return true;
    // Check specific permission
    return permissions[tab.permission];
  });

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
            alt="JNT Logo" 
            className="admin-logo"
          />
          <h2 className="admin-brand">JNT Admin</h2>
        </div>
        
        {/* User info */}
        {currentUser && (
          <div className="admin-user-info">
            <span className="user-name-display">{currentUser.name || currentUser.email}</span>
            <span className={`user-role-display ${currentUser.role}`}>{currentUser.role}</span>
          </div>
        )}
        
        <nav className="admin-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`nav-${tab.id}`}
            >
              <tab.icon size={20} />
              <span>{tab.label}</span>
              {tab.badge > 0 && <span className="nav-badge">{tab.badge}</span>}
            </button>
          ))}
        </nav>
        
        <div className="admin-sidebar-footer">
          <Button onClick={handleLogout} className="admin-logout-btn" data-testid="logout-button">
            <LogOut size={18} />
            <span>Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-main-header">
          <h1 className="admin-page-title">
            {tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}
          </h1>
          <Button onClick={fetchAllData} className="refresh-btn">
            <RefreshCw size={18} />
            Refresh
          </Button>
        </header>

        <div className="admin-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <div className="overview-content">
              {/* Itinerary Leads Stats - Primary Focus */}
              <div className="overview-section-header">
                <h2><Briefcase size={22} /> Itinerary Leads Overview</h2>
              </div>
              {itineraryStats && (
                <div className="stats-grid">
                  <div className="stat-card stat-total">
                    <h3>Total Leads</h3>
                    <p className="stat-number">{itineraryStats.total_leads || 0}</p>
                  </div>
                  <div className="stat-card stat-new">
                    <h3>Itineraries Generated</h3>
                    <p className="stat-number">{itineraryStats.itineraries_generated || 0}</p>
                  </div>
                  <div className="stat-card stat-contacted">
                    <h3>Callback Requests</h3>
                    <p className="stat-number">{itineraryStats.callback_requests || 0}</p>
                  </div>
                  <div className="stat-card stat-completed">
                    <h3>Conversion Rate</h3>
                    <p className="stat-number">{itineraryStats.conversion_rate || '0%'}</p>
                  </div>
                </div>
              )}
              
              {/* Quick Actions - Recent Leads */}
              <AdminErrorBoundary name="Quick Actions">
              <QuickActionsPanel token={token} />
              </AdminErrorBoundary>
              
              {/* General Inquiries Stats - Secondary */}
              <div className="overview-section-header" style={{marginTop: '2rem'}}>
                <h2><MessageSquare size={22} /> General Inquiries</h2>
              </div>
              <div className="stats-grid secondary-stats">
                <div className="stat-card stat-small">
                  <h3>Total Inquiries</h3>
                  <p className="stat-number">{stats.total_inquiries}</p>
                </div>
                <div className="stat-card stat-small">
                  <h3>New</h3>
                  <p className="stat-number">{stats.new_inquiries}</p>
                </div>
                <div className="stat-card stat-small">
                  <h3>Contacted</h3>
                  <p className="stat-number">{stats.contacted_inquiries}</p>
                </div>
                <div className="stat-card stat-small">
                  <h3>Completed</h3>
                  <p className="stat-number">{stats.completed_inquiries}</p>
                </div>
              </div>
              
              <div className="overview-actions">
                <div className="action-card">
                  <h3>Content Management</h3>
                  <p>Manage your website's experiences, testimonials, and JNT Tales content.</p>
                  <div className="action-stats">
                    <span><Compass size={16} /> {stats.total_experiences || 0} Experiences</span>
                    <span><Quote size={16} /> {stats.total_testimonials || 0} Testimonials</span>
                    <span><FileText size={16} /> {stats.total_blog_posts || 0} Blog Posts</span>
                    <span><Image size={16} /> {stats.total_gallery_items || 0} Gallery Items</span>
                    <span><Users size={16} /> {stats.total_users || 0} Users</span>
                    <span><Globe size={16} /> {countries?.length || 0} Countries</span>
                  </div>
                  {stats.total_experiences === 0 && (
                    <Button onClick={seedData} className="seed-btn" data-testid="seed-data-button">
                      <Plus size={18} />
                      Initialize Default Content
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Itinerary Leads Tab - CRM Dashboard */}
          {activeTab === 'leads' && (
            <div className="leads-content" data-testid="leads-tab">
              {/* Stats Cards */}
              {itineraryStats && (
                <div className="leads-stats-grid">
                  <div className="leads-stat-card">
                    <div className="leads-stat-icon" style={{ background: '#4299e1' }}>
                      <Users size={24} />
                    </div>
                    <div className="leads-stat-info">
                      <span className="leads-stat-number">{itineraryStats.total_leads}</span>
                      <span className="leads-stat-label">Total Leads</span>
                    </div>
                  </div>
                  <div className="leads-stat-card">
                    <div className="leads-stat-icon" style={{ background: '#48bb78' }}>
                      <CheckCircle size={24} />
                    </div>
                    <div className="leads-stat-info">
                      <span className="leads-stat-number">{itineraryStats.itineraries_generated}</span>
                      <span className="leads-stat-label">Itineraries Generated</span>
                    </div>
                  </div>
                  <div className="leads-stat-card">
                    <div className="leads-stat-icon" style={{ background: '#ed8936' }}>
                      <PhoneCall size={24} />
                    </div>
                    <div className="leads-stat-info">
                      <span className="leads-stat-number">{itineraryStats.callback_requests || 0}</span>
                      <span className="leads-stat-label">Callbacks</span>
                    </div>
                  </div>
                  <div className="leads-stat-card">
                    <div className="leads-stat-icon" style={{ background: '#38a169' }}>
                      <TrendingUp size={24} />
                    </div>
                    <div className="leads-stat-info">
                      <span className="leads-stat-number">{itineraryStats.conversion_rate}</span>
                      <span className="leads-stat-label">Conversion Rate</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Filter Bar with Bulk Actions */}
              <div className="leads-filter-bar">
                <div className="filter-tabs">
                  {['all', 'completed', 'callback_requested', 'contacted', 'converted', 'lost'].map(filter => (
                    <button 
                      key={filter}
                      className={`filter-tab ${leadsFilter === filter ? 'active' : ''}`}
                      onClick={() => setLeadsFilter(filter)}
                    >
                      {filter === 'all' ? 'All Leads' : getStatusLabel(filter)}
                      {filter === 'all' && <span className="filter-count">{itineraryLeads.length}</span>}
                    </button>
                  ))}
                </div>
                
                {/* Bulk Actions */}
                <div className="bulk-actions">
                  <label className="select-all-checkbox">
                    <input 
                      type="checkbox" 
                      checked={filteredLeads.length > 0 && selectedLeadIds.length === filteredLeads.length}
                      onChange={selectAllLeads}
                    />
                    <span>Select All ({selectedLeadIds.length})</span>
                  </label>
                  {selectedLeadIds.length > 0 && (
                    <button 
                      className="bulk-delete-btn"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleting}
                    >
                      <Trash2 size={16} />
                      {bulkDeleting ? 'Deleting...' : `Delete (${selectedLeadIds.length})`}
                    </button>
                  )}
                </div>
              </div>

              {/* Leads List and Detail View */}
              <div className="leads-layout">
                {/* Leads List */}
                <div className="leads-list-panel">
                  {filteredLeads.length === 0 ? (
                    <div className="empty-state">
                      <Briefcase size={48} />
                      <p>No leads found</p>
                    </div>
                  ) : (
                    <div className="leads-list">
                      {filteredLeads.map(lead => {
                        const requestData = lead.request_data || {};
                        return (
                          <div 
                            key={lead.id} 
                            className={`lead-card ${selectedLead?.id === lead.id ? 'selected' : ''} ${selectedLeadIds.includes(lead.id) ? 'checked' : ''}`}
                            data-testid={`lead-${lead.id}`}
                          >
                            <div className="lead-card-checkbox" onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox"
                                checked={selectedLeadIds.includes(lead.id)}
                                onChange={() => toggleLeadSelection(lead.id)}
                              />
                            </div>
                            <div className="lead-card-content" onClick={() => setSelectedLead(lead)}>
                              <div className="lead-card-header">
                                <div className="lead-avatar">
                                  {(requestData.first_name || 'U')[0].toUpperCase()}
                                </div>
                                <div className="lead-basic-info">
                                  <h4>{requestData.first_name} {requestData.last_name}</h4>
                                  <span className="lead-destination">{requestData.destination || 'Not specified'}</span>
                                </div>
                                <span 
                                  className="lead-status-badge"
                                  style={{ background: getStatusColor(lead.status) }}
                                >
                                  {getStatusLabel(lead.status)}
                                </span>
                              </div>
                              <div className="lead-card-meta">
                                <span><Calendar size={12} /> {formatDate(lead.created_at)}</span>
                                <span><DollarSign size={12} /> {requestData.budget_range || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Lead Detail Panel */}
                {selectedLead ? (
                  <div className="lead-detail-panel">
                    <div className="lead-detail-header">
                      <div className="lead-detail-title">
                        <div className="lead-avatar large">
                          {(selectedLead.request_data?.first_name || 'U')[0].toUpperCase()}
                        </div>
                        <div>
                          <h2>{selectedLead.request_data?.first_name} {selectedLead.request_data?.last_name}</h2>
                          <span className="lead-detail-destination">{selectedLead.request_data?.destination}</span>
                        </div>
                      </div>
                      <div className="lead-detail-actions">
                        {selectedLead.itinerary_pdf_path && (
                          <>
                            <a 
                              href={`${BACKEND_URL}/api/itinerary/download/${selectedLead.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-pdf-btn"
                              data-testid="download-pdf-btn"
                            >
                              <Download size={16} />
                              PDF
                            </a>
                            <a 
                              href={`${BACKEND_URL}/api/itinerary/download-docx/${selectedLead.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-docx-btn"
                              data-testid="download-docx-btn"
                            >
                              <FileText size={16} />
                              Word
                            </a>
                          </>
                        )}
                        <button 
                          onClick={() => deleteLead(selectedLead.id)}
                          className="delete-lead-btn"
                          title="Delete this lead"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="lead-section">
                      <h3><Users size={18} /> Contact Information</h3>
                      <div className="lead-info-grid">
                        <div className="lead-info-item">
                          <Mail size={16} />
                          <a href={`mailto:${selectedLead.request_data?.email}`}>{selectedLead.request_data?.email}</a>
                        </div>
                        <div className="lead-info-item">
                          <Phone size={16} />
                          <a href={`tel:${selectedLead.request_data?.phone}`}>{selectedLead.request_data?.phone}</a>
                        </div>
                        {selectedLead.request_data?.request_callback && (
                          <div className="lead-info-item callback-alert">
                            <PhoneCall size={16} />
                            <span>Callback Requested: {selectedLead.request_data?.callback_time || 'Anytime'}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Trip Details */}
                    <div className="lead-section">
                      <h3><MapPin size={18} /> Trip Details</h3>
                      <div className="lead-details-grid">
                        <div className="detail-item">
                          <label>Destination</label>
                          <span>{selectedLead.request_data?.destination || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Origin</label>
                          <span>{selectedLead.request_data?.origin_city || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Travel Dates</label>
                          <span>{selectedLead.request_data?.travel_start_date} to {selectedLead.request_data?.travel_end_date}</span>
                        </div>
                        <div className="detail-item">
                          <label>Travelers</label>
                          <span>{selectedLead.request_data?.num_travelers || 2}</span>
                        </div>
                        <div className="detail-item">
                          <label>Budget</label>
                          <span>INR {selectedLead.request_data?.budget_range || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Travel Style</label>
                          <span>{selectedLead.request_data?.travel_style || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Accommodation</label>
                          <span>{selectedLead.request_data?.accommodation_type || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Food Preference</label>
                          <span>{selectedLead.request_data?.food_preference || 'N/A'}</span>
                        </div>
                        {selectedLead.request_data?.nights_per_destination && (
                          <div className="detail-item">
                            <label>Nights Per Destination</label>
                            <span>{selectedLead.request_data.nights_per_destination}</span>
                          </div>
                        )}
                        {selectedLead.request_data?.flight_class && (
                          <div className="detail-item">
                            <label>Flight Class</label>
                            <span>{selectedLead.request_data.flight_class}</span>
                          </div>
                        )}
                        {selectedLead.request_data?.special_interests?.length > 0 && (
                          <div className="detail-item full-width">
                            <label>Special Interests</label>
                            <div className="interests-tags">
                              {selectedLead.request_data.special_interests.map((interest, i) => (
                                <span key={i} className="interest-badge">{interest}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedLead.request_data?.special_requests && (
                          <div className="detail-item full-width">
                            <label>Special Requests</label>
                            <p>{selectedLead.request_data.special_requests}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Update */}
                    <div className="lead-section">
                      <h3><CheckCircle size={18} /> Update Status</h3>
                      <div className="status-buttons">
                        {['contacted', 'callback_requested', 'converted', 'lost'].map(status => (
                          <button
                            key={status}
                            className={`status-btn ${selectedLead.status === status ? 'active' : ''}`}
                            style={{ 
                              borderColor: getStatusColor(status),
                              background: selectedLead.status === status ? getStatusColor(status) : 'transparent',
                              color: selectedLead.status === status ? 'white' : getStatusColor(status)
                            }}
                            onClick={() => updateLeadStatus(selectedLead.id, status)}
                          >
                            {getStatusLabel(status)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Timeline / Notes */}
                    <div className="lead-section timeline-section">
                      <h3><Clock size={18} /> Timeline & Notes</h3>
                      
                      {/* Add Note Form */}
                      <div className="add-note-form">
                        <Textarea
                          placeholder="Add a note about this lead..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          rows={2}
                        />
                        <Button 
                          onClick={() => addNoteToLead(selectedLead.id)}
                          disabled={!newNote.trim()}
                          className="add-note-btn"
                        >
                          <Send size={16} />
                          Add Note
                        </Button>
                      </div>

                      {/* Timeline */}
                      <div className="timeline">
                        {/* Notes */}
                        {(selectedLead.notes || []).slice().reverse().map(note => (
                          <div key={note.id} className="timeline-item note">
                            <div className="timeline-icon">
                              <MessageSquare size={14} />
                            </div>
                            <div className="timeline-content">
                              <p>{note.note}</p>
                              <span className="timeline-meta">{formatDate(note.created_at)}</span>
                            </div>
                            <button 
                              className="delete-note-btn"
                              onClick={() => deleteNote(selectedLead.id, note.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        
                        {/* System Events */}
                        {selectedLead.itinerary_generated && (
                          <div className="timeline-item system">
                            <div className="timeline-icon success">
                              <CheckCircle size={14} />
                            </div>
                            <div className="timeline-content">
                              <p>Itinerary generated successfully</p>
                              <span className="timeline-meta">{formatDate(selectedLead.updated_at)}</span>
                            </div>
                          </div>
                        )}
                        
                        <div className="timeline-item system">
                          <div className="timeline-icon">
                            <Plus size={14} />
                          </div>
                          <div className="timeline-content">
                            <p>Lead created</p>
                            <span className="timeline-meta">{formatDate(selectedLead.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="lead-detail-panel empty">
                    <div className="empty-detail-state">
                      <Briefcase size={64} />
                      <h3>Select a Lead</h3>
                      <p>Click on a lead from the list to view details and manage the conversation</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && (
            <div className="inquiries-content">
              {inquiries.length === 0 ? (
                <div className="empty-state">
                  <MessageSquare size={48} />
                  <p>No inquiries yet</p>
                </div>
              ) : (
                <div className="inquiries-list">
                  {inquiries.map(inquiry => (
                    <div key={inquiry.id} className="inquiry-card" data-testid={`inquiry-${inquiry.id}`}>
                      <div className="inquiry-header" onClick={() => setExpandedInquiry(expandedInquiry === inquiry.id ? null : inquiry.id)}>
                        <div className="inquiry-info">
                          <h3>{inquiry.name}</h3>
                          <span className={`status-badge status-${inquiry.status}`}>{inquiry.status}</span>
                        </div>
                        <div className="inquiry-meta">
                          <span><Calendar size={14} /> {new Date(inquiry.created_at).toLocaleDateString()}</span>
                          {expandedInquiry === inquiry.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </div>
                      
                      {expandedInquiry === inquiry.id && (
                        <div className="inquiry-details">
                          <div className="inquiry-contact">
                            <p><Mail size={14} /> {inquiry.email}</p>
                            <p><Phone size={14} /> {inquiry.phone}</p>
                          </div>
                          <div className="inquiry-travel-info">
                            {inquiry.destination && <p><MapPin size={14} /> {inquiry.destination}</p>}
                            {inquiry.travel_style && <p><Compass size={14} /> {inquiry.travel_style}</p>}
                            {inquiry.travel_dates && <p><Calendar size={14} /> {inquiry.travel_dates}</p>}
                            {inquiry.number_of_guests && <p><Users size={14} /> {inquiry.number_of_guests} guests</p>}
                            {inquiry.budget_range && <p><DollarSign size={14} /> {inquiry.budget_range}</p>}
                          </div>
                          {inquiry.special_requests && (
                            <div className="inquiry-requests">
                              <strong>Special Requests:</strong>
                              <p>{inquiry.special_requests}</p>
                            </div>
                          )}
                          <div className="inquiry-actions">
                            <Select value={inquiry.status} onValueChange={(value) => updateInquiryStatus(inquiry.id, value)}>
                              <SelectTrigger className="status-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                            {currentUser?.role !== 'staff' && <Button onClick={() => deleteInquiry(inquiry.id)} className="delete-btn">
                              <Trash2 size={16} />
                            </Button>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Experiences Tab */}
          {activeTab === 'experiences' && (
            <div className="experiences-content">
              <div className="content-header">
                <Button 
                  onClick={() => setNewExperience({ title: '', description: '', image: '', icon: 'Plane', features: [], instagram_link: '', is_featured: true, order: experiences.length + 1 })}
                  className="add-btn"
                  data-testid="add-experience-button"
                >
                  <Plus size={18} />
                  Add Experience
                </Button>
              </div>

              {newExperience && (
                <ExperienceForm 
                  experience={newExperience}
                  onSave={saveExperience}
                  onCancel={() => setNewExperience(null)}
                  isNew={true}
                  token={token}
                />
              )}

              <div className="experiences-grid">
                {experiences.map(experience => (
                  <div key={experience.id} className="experience-admin-card" data-testid={`experience-${experience.id}`}>
                    {editingExperience === experience.id ? (
                      <ExperienceForm 
                        experience={experience}
                        onSave={saveExperience}
                        onCancel={() => setEditingExperience(null)}
                        token={token}
                      />
                    ) : (
                      <>
                        <div className="card-image">
                          <img src={experience.image} alt={experience.title} />
                        </div>
                        <div className="card-content">
                          <h3>{experience.title}</h3>
                          <p className="description">{experience.description}</p>
                          <div className="card-meta">
                            <span>Icon: {experience.icon}</span>
                            <span>{experience.features?.length || 0} features</span>
                          </div>
                        </div>
                        <div className="card-actions">
                          <Button onClick={() => setEditingExperience(experience.id)} className="edit-btn">
                            <Edit2 size={16} /> Edit
                          </Button>
                          <Button onClick={() => duplicateExperience(experience)} className="duplicate-btn" title="Duplicate">
                            <Copy size={16} />
                          </Button>
                          {currentUser?.role !== 'staff' && (
                            <Button onClick={() => deleteExperience(experience.id)} className="delete-btn">
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {experiences.length === 0 && !newExperience && (
                <div className="empty-state">
                  <Compass size={48} />
                  <p>No experiences yet</p>
                  <Button onClick={seedData} className="seed-btn">
                    Initialize Default Content
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <div className="testimonials-content">
              <div className="content-header">
                <Button 
                  onClick={() => setNewTestimonial({ client_name: '', quote: '', client_title: '', client_image: '', video_url: '', rating: 5, destination: '', experience_type: '', review_source: null, review_link: '', is_featured: true, order: testimonials.length + 1 })}
                  className="add-btn"
                  data-testid="add-testimonial-button"
                >
                  <Plus size={18} />
                  Add Testimonial
                </Button>
              </div>

              {newTestimonial && (
                <TestimonialForm 
                  testimonial={newTestimonial}
                  onSave={saveTestimonial}
                  onCancel={() => setNewTestimonial(null)}
                  isNew={true}
                  token={token}
                />
              )}

              <div className="testimonials-grid">
                {testimonials.map(testimonial => (
                  <div key={testimonial.id} className="testimonial-admin-card" data-testid={`testimonial-${testimonial.id}`}>
                    {editingTestimonial === testimonial.id ? (
                      <TestimonialForm 
                        testimonial={testimonial}
                        onSave={saveTestimonial}
                        onCancel={() => setEditingTestimonial(null)}
                        token={token}
                      />
                    ) : (
                      <>
                        <div className="testimonial-card-header">
                          {testimonial.client_image && (
                            <img src={testimonial.client_image} alt={testimonial.client_name} className="testimonial-avatar" />
                          )}
                          <div>
                            <h3>{testimonial.client_name}</h3>
                            {testimonial.client_title && <p className="client-title">{testimonial.client_title}</p>}
                          </div>
                        </div>
                        <div className="testimonial-card-content">
                          <div className="rating-stars">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={16} fill={i < testimonial.rating ? "#b8976c" : "none"} color="#b8976c" />
                            ))}
                          </div>
                          <p className="quote">"{testimonial.quote}"</p>
                          {testimonial.video_url && (
                            <div className="video-indicator">
                              <Video size={16} /> Video testimonial available
                            </div>
                          )}
                          <div className="card-meta">
                            {testimonial.destination && <span>{testimonial.destination}</span>}
                            {testimonial.experience_type && <span>{testimonial.experience_type}</span>}
                          </div>
                        </div>
                        <div className="card-actions">
                          <Button onClick={() => setEditingTestimonial(testimonial.id)} className="edit-btn">
                            <Edit2 size={16} /> Edit
                          </Button>
                          {currentUser?.role !== 'staff' && (
                            <Button onClick={() => deleteTestimonial(testimonial.id)} className="delete-btn">
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {testimonials.length === 0 && !newTestimonial && (
                <div className="empty-state">
                  <Quote size={48} />
                  <p>No testimonials yet</p>
                  <p className="empty-hint">Add testimonials from happy clients to build trust</p>
                </div>
              )}
            </div>
          )}

          {/* Travel Gallery Tab */}
          {activeTab === 'travel-gallery' && (
            <div className="travel-gallery-content">
              <div className="section-header">
                <h3><Image size={20} /> Travel Gallery</h3>
                <p className="section-description">Manage photos and videos displayed below testimonials on homepage (max 12 images, 3 videos)</p>
                <Button 
                  onClick={() => setNewGalleryItem({ media_url: '', media_type: 'image', caption: '', destination: '', order: travelGallery.length + 1, is_featured: true })}
                  className="add-btn"
                  data-testid="add-gallery-btn"
                >
                  <Plus size={16} /> Add Media
                </Button>
              </div>

              {/* New Gallery Item Form */}
              {newGalleryItem && (
                <GalleryItemForm 
                  item={newGalleryItem}
                  onSave={saveGalleryItem}
                  onCancel={() => setNewGalleryItem(null)}
                  isNew={true}
                  token={token}
                />
              )}

              {/* Gallery Grid */}
              <div className="gallery-grid">
                {travelGallery.map((item) => (
                  <div key={item.id} className="gallery-item-card" data-testid={`gallery-item-${item.id}`}>
                    {editingGalleryItem === item.id ? (
                      <GalleryItemForm 
                        item={item}
                        onSave={saveGalleryItem}
                        onCancel={() => setEditingGalleryItem(null)}
                        isNew={false}
                        token={token}
                      />
                    ) : (
                      <>
                        <div className="gallery-media-preview">
                          {item.media_type === 'video' ? (
                            <video src={item.media_url} className="gallery-preview-video" />
                          ) : (
                            <img src={item.media_url} alt={item.caption || 'Gallery'} className="gallery-preview-image" />
                          )}
                          <span className={`media-type-badge ${item.media_type}`}>
                            {item.media_type === 'video' ? <Video size={12} /> : <Image size={12} />}
                            {item.media_type}
                          </span>
                        </div>
                        <div className="gallery-item-info">
                          {item.caption && <p className="gallery-caption">{item.caption}</p>}
                          {item.destination && <span className="gallery-destination">{item.destination}</span>}
                        </div>
                        <div className="card-actions">
                          <Button onClick={() => setEditingGalleryItem(item.id)} className="edit-btn">
                            <Edit2 size={16} /> Edit
                          </Button>
                          {currentUser?.role !== 'staff' && (
                            <Button onClick={() => deleteGalleryItem(item.id)} className="delete-btn">
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {travelGallery.length === 0 && !newGalleryItem && (
                <div className="empty-state">
                  <Image size={48} />
                  <p>No gallery items yet</p>
                  <p className="empty-hint">Add photos and videos from your travels to showcase your journeys</p>
                </div>
              )}
            </div>
          )}

          {/* JNT Tales Tab - Countries & Cities Management */}
          {activeTab === 'jnt-tales' && (
            <div className="blog-management">
              {/* Sub-tabs for Countries and Cities */}
              <div className="jnt-tales-subtabs">
                <button 
                  className={`subtab-btn ${jntTalesSubTab === 'countries' ? 'active' : ''}`}
                  onClick={() => setJntTalesSubTab('countries')}
                >
                  <Globe size={18} /> Countries
                </button>
                <button 
                  className={`subtab-btn ${jntTalesSubTab === 'cities' ? 'active' : ''}`}
                  onClick={() => setJntTalesSubTab('cities')}
                >
                  <MapPin size={18} /> Cities/Destinations
                </button>
              </div>

              {/* Countries Sub-section */}
              {jntTalesSubTab === 'countries' && (
                <>
                  <div className="section-header-with-actions">
                    <div>
                      <h3><Globe size={20} /> Countries Management</h3>
                      <p className="section-description">Manage countries for the Continent → Country → City hierarchy</p>
                    </div>
                    <Button 
                      onClick={() => setNewCountry({
                        name: '',
                        slug: '',
                        continent: 'Asia',
                        hero_image: '',
                        image_alt: '',
                        description: '',
                        highlights: '',
                        is_featured: false,
                        order: countries.length
                      })} 
                      className="add-btn"
                      data-testid="add-country-btn"
                    >
                      <Plus size={16} /> Add Country
                    </Button>
                  </div>

                  {/* Continent Filter */}
                  <div className="blog-filters">
                    <button 
                      className={`filter-btn ${countryFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setCountryFilter('all')}
                    >
                      All ({countries.length})
                    </button>
                    {['Asia', 'Europe', 'Africa', 'North America', 'South America', 'Oceania'].map(continent => {
                      const count = countries.filter(c => c.continent === continent).length;
                      return (
                        <button 
                          key={continent}
                          className={`filter-btn ${countryFilter === continent.toLowerCase().replace(/\s+/g, '-') ? 'active' : ''}`}
                          onClick={() => setCountryFilter(continent.toLowerCase().replace(/\s+/g, '-'))}
                        >
                          {continent} ({count})
                        </button>
                      );
                    })}
                  </div>

                  {/* New Country Form */}
                  {newCountry && (
                    <div className="blog-post-form" data-testid="new-country-form">
                      <h4>New Country</h4>
                      <div className="form-grid">
                        <div className="form-field">
                          <label>Country Name *</label>
                          <Input
                            value={newCountry.name}
                            onChange={(e) => setNewCountry({...newCountry, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')})}
                            placeholder="e.g., Indonesia, Italy, Kenya"
                          />
                        </div>
                        <div className="form-field">
                          <label>Continent *</label>
                          <Select value={newCountry.continent} onValueChange={(v) => setNewCountry({...newCountry, continent: v})}>
                            <SelectTrigger><SelectValue placeholder="Select continent" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Asia">Asia</SelectItem>
                              <SelectItem value="Europe">Europe</SelectItem>
                              <SelectItem value="Africa">Africa</SelectItem>
                              <SelectItem value="North America">North America</SelectItem>
                              <SelectItem value="South America">South America</SelectItem>
                              <SelectItem value="Oceania">Oceania</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="form-field full-width">
                          <label>Hero Image</label>
                          <MediaUpload
                            value={newCountry.hero_image}
                            onChange={(url) => setNewCountry({...newCountry, hero_image: url})}
                            label="Hero Image"
                            token={token}
                          />
                        </div>
                        <div className="form-field full-width">
                          <label>Description *</label>
                          <Textarea
                            value={newCountry.description}
                            onChange={(e) => setNewCountry({...newCountry, description: e.target.value})}
                            placeholder="A compelling description of the country..."
                            rows={3}
                          />
                        </div>
                        <div className="form-field full-width">
                          <label>Highlights</label>
                          <Textarea
                            value={newCountry.highlights}
                            onChange={(e) => setNewCountry({...newCountry, highlights: e.target.value})}
                            placeholder="Key highlights and attractions..."
                            rows={2}
                          />
                        </div>
                        <div className="form-field">
                          <label>Featured on Homepage</label>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={newCountry.is_featured}
                              onChange={(e) => setNewCountry({...newCountry, is_featured: e.target.checked})}
                            />
                            Show in Featured Destinations
                          </label>
                        </div>
                      </div>
                      <div className="form-actions">
                        <Button onClick={() => saveCountry(newCountry)} className="save-btn" data-testid="save-new-country-btn">
                          <Save size={16} /> Save Country
                        </Button>
                        <Button onClick={() => setNewCountry(null)} className="cancel-btn">
                          <X size={16} /> Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Countries List */}
                  <div className="blog-posts-list">
                    {getFilteredCountries().length === 0 ? (
                      <div className="empty-state">
                        <Globe size={48} />
                        <p>No countries yet</p>
                        <p className="empty-hint">Add countries to organize your destinations</p>
                      </div>
                    ) : (
                      getFilteredCountries().map(country => (
                        <div key={country.id} className="blog-post-item" data-testid={`country-item-${country.id}`}>
                          {editingCountry?.id === country.id ? (
                            <div className="blog-post-form editing">
                              <div className="form-grid">
                                <div className="form-field">
                                  <label>Country Name</label>
                                  <Input
                                    value={editingCountry.name}
                                    onChange={(e) => setEditingCountry({...editingCountry, name: e.target.value})}
                                  />
                                </div>
                                <div className="form-field">
                                  <label>Continent</label>
                                  <Select value={editingCountry.continent} onValueChange={(v) => setEditingCountry({...editingCountry, continent: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Asia">Asia</SelectItem>
                                      <SelectItem value="Europe">Europe</SelectItem>
                                      <SelectItem value="Africa">Africa</SelectItem>
                                      <SelectItem value="North America">North America</SelectItem>
                                      <SelectItem value="South America">South America</SelectItem>
                                      <SelectItem value="Oceania">Oceania</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="form-field full-width">
                                  <label>Hero Image</label>
                                  <MediaUpload
                                    value={editingCountry.hero_image}
                                    onChange={(url) => setEditingCountry({...editingCountry, hero_image: url})}
                                    label="Hero Image"
                                    token={token}
                                  />
                                </div>
                                <div className="form-field full-width">
                                  <label>Description</label>
                                  <Textarea
                                    value={editingCountry.description}
                                    onChange={(e) => setEditingCountry({...editingCountry, description: e.target.value})}
                                    rows={3}
                                  />
                                </div>
                                <div className="form-field full-width">
                                  <label>Highlights</label>
                                  <Textarea
                                    value={editingCountry.highlights}
                                    onChange={(e) => setEditingCountry({...editingCountry, highlights: e.target.value})}
                                    rows={2}
                                  />
                                </div>
                                <div className="form-field">
                                  <label>Featured</label>
                                  <label className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={editingCountry.is_featured}
                                      onChange={(e) => setEditingCountry({...editingCountry, is_featured: e.target.checked})}
                                    />
                                    Show on homepage
                                  </label>
                                </div>
                              </div>
                              <div className="form-actions">
                                <Button onClick={() => saveCountry(editingCountry)} className="save-btn">
                                  <Save size={16} /> Save
                                </Button>
                                <Button onClick={() => setEditingCountry(null)} className="cancel-btn">
                                  <X size={16} /> Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="blog-post-card">
                              <div className="post-image">
                                {country.hero_image ? (
                                  <img src={country.hero_image} alt={country.name} />
                                ) : (
                                  <div className="post-image-placeholder">
                                    <Globe size={24} />
                                  </div>
                                )}
                              </div>
                              <div className="post-details">
                                <div className="post-header">
                                  <span className="post-continent">{country.continent}</span>
                                  {country.is_featured && <span className="post-featured"><Star size={12} /> Featured</span>}
                                </div>
                                <h4 className="post-title">{country.name}</h4>
                                <p className="post-intro">{country.description?.substring(0, 120)}...</p>
                                <div className="post-meta">
                                  <span><MapPin size={12} /> {blogPosts.filter(p => p.country_id === country.id).length} cities</span>
                                </div>
                              </div>
                              <div className="post-actions">
                                <Button onClick={() => setEditingCountry({...country})} className="edit-btn" title="Edit">
                                  <Edit2 size={16} />
                                </Button>
                                <Button onClick={() => duplicateCountry(country)} className="duplicate-btn" title="Duplicate">
                                  <Copy size={16} />
                                </Button>
                                {currentUser?.role !== 'staff' && (
                                  <Button onClick={() => deleteCountry(country.id)} className="delete-btn" title="Delete">
                                    <Trash2 size={16} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Cities Sub-section */}
              {jntTalesSubTab === 'cities' && (
                <>
                  <div className="section-header-with-actions">
                    <div>
                      <h3><MapPin size={20} /> Cities & Destinations</h3>
                      <p className="section-description">Manage city/destination blog posts under each country</p>
                    </div>
                    <Button 
                      onClick={() => setNewBlogPost({
                        title: '',
                        slug: '',
                        continent: 'Asia',
                        country_id: '',
                        featured_image: '',
                        image_alt: '',
                        introduction: '',
                        content: '',
                        best_time: '',
                        insider_tip: '',
                        jnt_experience: '',
                        hidden_gems: '',
                        getting_around: '',
                        best_cafes: '',
                        best_restaurants: '',
                        best_hotels: '',
                        photo_spots: '',
                        curated_experiences: '',
                        rail_cruise_tours: '',
                        indian_traveler_guide: '',
                        seo_keywords: '',
                        long_tail_keywords: '',
                        meta_title: '',
                        meta_description: '',
                        keywords: '',
                        is_featured: false,
                        order: blogPosts.length,
                        status: 'published'
                      })} 
                      className="add-btn"
                      data-testid="add-blog-post-btn"
                    >
                      <Plus size={16} /> Add City/Destination
                    </Button>
                  </div>

              {/* Continent Filter */}
              <div className="blog-filters">
                <button 
                  className={`filter-btn ${blogPostFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setBlogPostFilter('all')}
                >
                  All ({blogPosts.length})
                </button>
                {['Asia', 'Europe', 'Africa', 'North America', 'South America', 'Oceania'].map(continent => {
                  const count = blogPosts.filter(p => p.continent === continent).length;
                  return (
                    <button 
                      key={continent}
                      className={`filter-btn ${blogPostFilter === continent.toLowerCase().replace(/\s+/g, '-') ? 'active' : ''}`}
                      onClick={() => setBlogPostFilter(continent.toLowerCase().replace(/\s+/g, '-'))}
                    >
                      {continent} ({count})
                    </button>
                  );
                })}
              </div>

              {/* New Blog Post Form */}
              {newBlogPost && (
                <div className="blog-post-form" data-testid="new-blog-post-form">
                  <h4>New Blog Post</h4>
                  <div className="form-grid">
                    <div className="form-field">
                      <label>Title *</label>
                      <Input
                        value={newBlogPost.title}
                        onChange={(e) => setNewBlogPost({...newBlogPost, title: e.target.value, slug: generateSlug(e.target.value)})}
                        placeholder="Enter post title"
                      />
                    </div>
                    <div className="form-field">
                      <label>Slug</label>
                      <Input
                        value={newBlogPost.slug}
                        onChange={(e) => setNewBlogPost({...newBlogPost, slug: e.target.value})}
                        placeholder="auto-generated-from-title"
                      />
                    </div>
                    <div className="form-field">
                      <label>Continent *</label>
                      <Select value={newBlogPost.continent} onValueChange={(v) => setNewBlogPost({...newBlogPost, continent: v, country_id: ''})}>
                        <SelectTrigger><SelectValue placeholder="Select continent" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Asia">Asia</SelectItem>
                          <SelectItem value="Europe">Europe</SelectItem>
                          <SelectItem value="Africa">Africa</SelectItem>
                          <SelectItem value="North America">North America</SelectItem>
                          <SelectItem value="South America">South America</SelectItem>
                          <SelectItem value="Oceania">Oceania</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <label>Country *</label>
                      <Select value={newBlogPost.country_id} onValueChange={(v) => setNewBlogPost({...newBlogPost, country_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                        <SelectContent>
                          {countries.filter(c => c.continent === newBlogPost.continent).map(country => (
                            <SelectItem key={country.id} value={country.id}>{country.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <label>Status</label>
                      <Select value={newBlogPost.status} onValueChange={(v) => setNewBlogPost({...newBlogPost, status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field full-width">
                      <label>Featured Image</label>
                      <MediaUpload
                        value={newBlogPost.featured_image}
                        onChange={(url) => setNewBlogPost({...newBlogPost, featured_image: url})}
                        label="Featured Image"
                        token={token}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Image Alt Text</label>
                      <Input
                        value={newBlogPost.image_alt}
                        onChange={(e) => setNewBlogPost({...newBlogPost, image_alt: e.target.value})}
                        placeholder="Describe the image for accessibility"
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Introduction *</label>
                      <Textarea
                        value={newBlogPost.introduction}
                        onChange={(e) => setNewBlogPost({...newBlogPost, introduction: e.target.value})}
                        placeholder="A compelling introduction to the post..."
                        rows={3}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Main Content (HTML supported)</label>
                      <Textarea
                        value={newBlogPost.content}
                        onChange={(e) => setNewBlogPost({...newBlogPost, content: e.target.value})}
                        placeholder="<p>Your detailed content here...</p>"
                        rows={8}
                      />
                    </div>
                    <div className="form-field">
                      <label>Best Time to Visit</label>
                      <Input
                        value={newBlogPost.best_time}
                        onChange={(e) => setNewBlogPost({...newBlogPost, best_time: e.target.value})}
                        placeholder="e.g., March to May"
                      />
                    </div>
                    <div className="form-field">
                      <label>Featured Post</label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newBlogPost.is_featured}
                          onChange={(e) => setNewBlogPost({...newBlogPost, is_featured: e.target.checked})}
                        />
                        Show on homepage
                      </label>
                    </div>
                    <div className="form-field full-width">
                      <label>Insider Tip</label>
                      <Textarea
                        value={newBlogPost.insider_tip}
                        onChange={(e) => setNewBlogPost({...newBlogPost, insider_tip: e.target.value})}
                        placeholder="A special tip for travelers..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>JNT Experience</label>
                      <Textarea
                        value={newBlogPost.jnt_experience}
                        onChange={(e) => setNewBlogPost({...newBlogPost, jnt_experience: e.target.value})}
                        placeholder="What makes this destination special with JNT..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Hidden Gems</label>
                      <Textarea
                        value={newBlogPost.hidden_gems}
                        onChange={(e) => setNewBlogPost({...newBlogPost, hidden_gems: e.target.value})}
                        placeholder="Secret spots and lesser-known attractions..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                      <h4 style={{ margin: '0 0 12px', color: '#4a5568', fontSize: '14px', fontWeight: 600 }}>Detailed Content Sections</h4>
                    </div>
                    <div className="form-field full-width">
                      <label>Getting Around</label>
                      <Textarea
                        value={newBlogPost.getting_around}
                        onChange={(e) => setNewBlogPost({...newBlogPost, getting_around: e.target.value})}
                        placeholder="Transport tips, local commute info..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field">
                      <label>Best Cafes</label>
                      <Textarea
                        value={newBlogPost.best_cafes}
                        onChange={(e) => setNewBlogPost({...newBlogPost, best_cafes: e.target.value})}
                        placeholder="Top cafe recommendations..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field">
                      <label>Best Restaurants</label>
                      <Textarea
                        value={newBlogPost.best_restaurants}
                        onChange={(e) => setNewBlogPost({...newBlogPost, best_restaurants: e.target.value})}
                        placeholder="Must-visit restaurants..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field">
                      <label>Best Hotels</label>
                      <Textarea
                        value={newBlogPost.best_hotels}
                        onChange={(e) => setNewBlogPost({...newBlogPost, best_hotels: e.target.value})}
                        placeholder="Recommended stays..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field">
                      <label>Photo Spots</label>
                      <Textarea
                        value={newBlogPost.photo_spots}
                        onChange={(e) => setNewBlogPost({...newBlogPost, photo_spots: e.target.value})}
                        placeholder="Best places for photos..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Curated Experiences</label>
                      <Textarea
                        value={newBlogPost.curated_experiences}
                        onChange={(e) => setNewBlogPost({...newBlogPost, curated_experiences: e.target.value})}
                        placeholder="Handpicked experiences for travelers..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Rail / Cruise Tours</label>
                      <Textarea
                        value={newBlogPost.rail_cruise_tours}
                        onChange={(e) => setNewBlogPost({...newBlogPost, rail_cruise_tours: e.target.value})}
                        placeholder="Rail or cruise tour options..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Indian Traveler Guide</label>
                      <Textarea
                        value={newBlogPost.indian_traveler_guide}
                        onChange={(e) => setNewBlogPost({...newBlogPost, indian_traveler_guide: e.target.value})}
                        placeholder="Tips specifically for Indian travelers..."
                        rows={2}
                      />
                    </div>
                    <div className="form-field full-width" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                      <h4 style={{ margin: '0 0 12px', color: '#4a5568', fontSize: '14px', fontWeight: 600 }}>SEO Settings</h4>
                    </div>
                    <div className="form-field">
                      <label>SEO Keywords</label>
                      <Input
                        value={newBlogPost.seo_keywords}
                        onChange={(e) => setNewBlogPost({...newBlogPost, seo_keywords: e.target.value})}
                        placeholder="primary, seo, keywords"
                      />
                    </div>
                    <div className="form-field">
                      <label>Long-tail Keywords</label>
                      <Input
                        value={newBlogPost.long_tail_keywords}
                        onChange={(e) => setNewBlogPost({...newBlogPost, long_tail_keywords: e.target.value})}
                        placeholder="best cafes in paris, hidden gems rome"
                      />
                    </div>
                    <div className="form-field">
                      <label>Meta Title (SEO)</label>
                      <Input
                        value={newBlogPost.meta_title}
                        onChange={(e) => setNewBlogPost({...newBlogPost, meta_title: e.target.value})}
                        placeholder="SEO page title"
                      />
                    </div>
                    <div className="form-field">
                      <label>Keywords (SEO)</label>
                      <Input
                        value={newBlogPost.keywords}
                        onChange={(e) => setNewBlogPost({...newBlogPost, keywords: e.target.value})}
                        placeholder="keyword1, keyword2, keyword3"
                      />
                    </div>
                    <div className="form-field full-width">
                      <label>Meta Description (SEO)</label>
                      <Textarea
                        value={newBlogPost.meta_description}
                        onChange={(e) => setNewBlogPost({...newBlogPost, meta_description: e.target.value})}
                        placeholder="Brief description for search engines..."
                        rows={2}
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <Button onClick={() => saveBlogPost(newBlogPost)} className="save-btn" data-testid="save-new-post-btn">
                      <Save size={16} /> Save Post
                    </Button>
                    <Button onClick={() => setNewBlogPost(null)} className="cancel-btn">
                      <X size={16} /> Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Blog Posts List */}
              <div className="blog-posts-list">
                {getFilteredBlogPosts().length === 0 ? (
                  <div className="empty-state">
                    <Globe size={48} />
                    <p>No blog posts yet</p>
                    <p className="empty-hint">Start sharing your travel tales with the world</p>
                  </div>
                ) : (
                  getFilteredBlogPosts().map(post => (
                    <div key={post.id} className="blog-post-item" data-testid={`blog-post-item-${post.id}`}>
                      {editingBlogPost?.id === post.id ? (
                        <div className="blog-post-form editing">
                          <div className="form-grid">
                            <div className="form-field">
                              <label>Title</label>
                              <Input
                                value={editingBlogPost.title}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, title: e.target.value})}
                              />
                            </div>
                            <div className="form-field">
                              <label>Slug</label>
                              <Input
                                value={editingBlogPost.slug}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, slug: e.target.value})}
                              />
                            </div>
                            <div className="form-field">
                              <label>Continent</label>
                              <Select value={editingBlogPost.continent} onValueChange={(v) => setEditingBlogPost({...editingBlogPost, continent: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Asia">Asia</SelectItem>
                                  <SelectItem value="Europe">Europe</SelectItem>
                                  <SelectItem value="Africa">Africa</SelectItem>
                                  <SelectItem value="North America">North America</SelectItem>
                                  <SelectItem value="South America">South America</SelectItem>
                                  <SelectItem value="Oceania">Oceania</SelectItem>
                                  <SelectItem value="Antarctica">Antarctica</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="form-field">
                              <label>Status</label>
                              <Select value={editingBlogPost.status} onValueChange={(v) => setEditingBlogPost({...editingBlogPost, status: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="published">Published</SelectItem>
                                  <SelectItem value="draft">Draft</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="form-field full-width">
                              <label>Featured Image</label>
                              <MediaUpload
                                value={editingBlogPost.featured_image}
                                onChange={(url) => setEditingBlogPost({...editingBlogPost, featured_image: url})}
                                label="Featured Image"
                                token={token}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Introduction</label>
                              <Textarea
                                value={editingBlogPost.introduction}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, introduction: e.target.value})}
                                rows={3}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Main Content</label>
                              <Textarea
                                value={editingBlogPost.content}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, content: e.target.value})}
                                rows={6}
                              />
                            </div>
                            <div className="form-field">
                              <label>Best Time to Visit</label>
                              <Input
                                value={editingBlogPost.best_time}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, best_time: e.target.value})}
                              />
                            </div>
                            <div className="form-field">
                              <label>Featured</label>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={editingBlogPost.is_featured}
                                  onChange={(e) => setEditingBlogPost({...editingBlogPost, is_featured: e.target.checked})}
                                />
                                Show on homepage
                              </label>
                            </div>
                            <div className="form-field full-width">
                              <label>Insider Tip</label>
                              <Textarea
                                value={editingBlogPost.insider_tip}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, insider_tip: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>JNT Experience</label>
                              <Textarea
                                value={editingBlogPost.jnt_experience}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, jnt_experience: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Hidden Gems</label>
                              <Textarea
                                value={editingBlogPost.hidden_gems}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, hidden_gems: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field">
                              <label>Country</label>
                              <Select value={editingBlogPost.country_id} onValueChange={(v) => setEditingBlogPost({...editingBlogPost, country_id: v})}>
                                <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                                <SelectContent>
                                  {countries.filter(c => c.continent === editingBlogPost.continent).map(country => (
                                    <SelectItem key={country.id} value={country.id}>{country.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="form-field full-width" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                              <h4 style={{ margin: '0 0 12px', color: '#4a5568', fontSize: '14px', fontWeight: 600 }}>Detailed Content Sections</h4>
                            </div>
                            <div className="form-field full-width">
                              <label>Getting Around</label>
                              <Textarea
                                value={editingBlogPost.getting_around || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, getting_around: e.target.value})}
                                placeholder="Transport tips, local commute info..."
                                rows={2}
                              />
                            </div>
                            <div className="form-field">
                              <label>Best Cafes</label>
                              <Textarea
                                value={editingBlogPost.best_cafes || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, best_cafes: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field">
                              <label>Best Restaurants</label>
                              <Textarea
                                value={editingBlogPost.best_restaurants || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, best_restaurants: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field">
                              <label>Best Hotels</label>
                              <Textarea
                                value={editingBlogPost.best_hotels || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, best_hotels: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field">
                              <label>Photo Spots</label>
                              <Textarea
                                value={editingBlogPost.photo_spots || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, photo_spots: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Curated Experiences</label>
                              <Textarea
                                value={editingBlogPost.curated_experiences || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, curated_experiences: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Rail / Cruise Tours</label>
                              <Textarea
                                value={editingBlogPost.rail_cruise_tours || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, rail_cruise_tours: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Indian Traveler Guide</label>
                              <Textarea
                                value={editingBlogPost.indian_traveler_guide || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, indian_traveler_guide: e.target.value})}
                                rows={2}
                              />
                            </div>
                            <div className="form-field full-width" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                              <h4 style={{ margin: '0 0 12px', color: '#4a5568', fontSize: '14px', fontWeight: 600 }}>SEO Settings</h4>
                            </div>
                            <div className="form-field">
                              <label>SEO Keywords</label>
                              <Input
                                value={editingBlogPost.seo_keywords || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, seo_keywords: e.target.value})}
                                placeholder="primary, seo, keywords"
                              />
                            </div>
                            <div className="form-field">
                              <label>Long-tail Keywords</label>
                              <Input
                                value={editingBlogPost.long_tail_keywords || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, long_tail_keywords: e.target.value})}
                                placeholder="best cafes in paris, hidden gems rome"
                              />
                            </div>
                            <div className="form-field">
                              <label>Meta Title</label>
                              <Input
                                value={editingBlogPost.meta_title || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, meta_title: e.target.value})}
                              />
                            </div>
                            <div className="form-field">
                              <label>Keywords</label>
                              <Input
                                value={editingBlogPost.keywords || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, keywords: e.target.value})}
                              />
                            </div>
                            <div className="form-field full-width">
                              <label>Meta Description</label>
                              <Textarea
                                value={editingBlogPost.meta_description || ''}
                                onChange={(e) => setEditingBlogPost({...editingBlogPost, meta_description: e.target.value})}
                                rows={2}
                              />
                            </div>
                          </div>
                          <div className="form-actions">
                            <Button onClick={() => saveBlogPost(editingBlogPost)} className="save-btn">
                              <Save size={16} /> Save
                            </Button>
                            <Button onClick={() => setEditingBlogPost(null)} className="cancel-btn">
                              <X size={16} /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="blog-post-card">
                          <div className="post-image">
                            {post.featured_image ? (
                              <img src={post.featured_image} alt={post.title} />
                            ) : (
                              <div className="post-image-placeholder">
                                <Globe size={24} />
                              </div>
                            )}
                          </div>
                          <div className="post-details">
                            <div className="post-header">
                              <span className={`post-status ${post.status}`}>{post.status}</span>
                              <span className="post-continent">{post.continent}</span>
                              {post.is_featured && <span className="post-featured"><Star size={12} /> Featured</span>}
                            </div>
                            <h4 className="post-title">{post.title}</h4>
                            <p className="post-intro">{post.introduction?.substring(0, 120)}...</p>
                            <div className="post-meta">
                              {post.best_time && <span><Clock size={12} /> {post.best_time}</span>}
                            </div>
                          </div>
                          <div className="post-actions">
                            <Button onClick={() => setEditingBlogPost({...post})} className="edit-btn" title="Edit">
                              <Edit2 size={16} />
                            </Button>
                            <Button onClick={() => duplicateBlogPost(post)} className="duplicate-btn" title="Duplicate">
                              <Copy size={16} />
                            </Button>
                            {currentUser?.role !== 'staff' && (
                              <Button onClick={() => deleteBlogPost(post.id)} className="delete-btn" title="Delete">
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
                </>
              )}
            </div>
          )}

          {/* Site Content Tab */}
          {activeTab === 'content' && (
            <div className="site-content">
              {/* Hero Section */}
              <div className="content-section">
                <div className="section-header">
                  <h3><Image size={20} /> Hero Section</h3>
                  {!editingHero ? (
                    <Button onClick={() => setEditingHero(true)} className="edit-btn">
                      <Edit2 size={16} /> Edit
                    </Button>
                  ) : (
                    <div className="edit-actions">
                      <Button onClick={saveHeroContent} className="save-btn">
                        <Save size={16} /> Save
                      </Button>
                      <Button onClick={() => setEditingHero(false)} className="cancel-btn">
                        <X size={16} />
                      </Button>
                    </div>
                  )}
                </div>
                {heroContent && (
                  <div className="content-fields">
                    <div className="field">
                      <label>Headline</label>
                      {editingHero ? (
                        <Input 
                          value={heroContent.headline || ''} 
                          onChange={(e) => setHeroContent({...heroContent, headline: e.target.value})}
                        />
                      ) : (
                        <p>{heroContent.headline}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>Subheadline</label>
                      {editingHero ? (
                        <Input 
                          value={heroContent.subheadline || ''} 
                          onChange={(e) => setHeroContent({...heroContent, subheadline: e.target.value})}
                        />
                      ) : (
                        <p>{heroContent.subheadline}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>CTA Text</label>
                      {editingHero ? (
                        <Input 
                          value={heroContent.cta_text || ''} 
                          onChange={(e) => setHeroContent({...heroContent, cta_text: e.target.value})}
                        />
                      ) : (
                        <p>{heroContent.cta_text}</p>
                      )}
                    </div>
                    <div className="field full-width">
                      {editingHero ? (
                        <ImageUpload 
                          label="Hero Image (Primary/Fallback)"
                          value={heroContent.hero_image || ''} 
                          onChange={(url) => setHeroContent({...heroContent, hero_image: url})}
                          token={token}
                        />
                      ) : (
                        <>
                          <label>Hero Image</label>
                          <div className="image-preview">
                            <img src={heroContent.hero_image} alt="Hero" />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="field full-width">
                      <label>Carousel Images (used instead of primary image when set)</label>
                      {editingHero ? (
                        <div className="carousel-images-editor" data-testid="carousel-images-editor">
                          {(heroContent.hero_images || []).map((img, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <ImageUpload
                                  label={`Carousel Image ${idx + 1}`}
                                  value={img}
                                  onChange={(url) => {
                                    const updated = [...(heroContent.hero_images || [])];
                                    updated[idx] = url;
                                    setHeroContent({...heroContent, hero_images: updated});
                                  }}
                                  token={token}
                                />
                              </div>
                              <Button
                                onClick={() => {
                                  const updated = (heroContent.hero_images || []).filter((_, i) => i !== idx);
                                  setHeroContent({...heroContent, hero_images: updated.length > 0 ? updated : null});
                                }}
                                className="delete-btn"
                                style={{ marginTop: '24px' }}
                                title="Remove image"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          ))}
                          <Button
                            onClick={() => {
                              const current = heroContent.hero_images || [];
                              setHeroContent({...heroContent, hero_images: [...current, '']});
                            }}
                            className="edit-btn"
                            style={{ marginTop: '4px' }}
                            data-testid="add-carousel-image-btn"
                          >
                            <Plus size={14} /> Add Carousel Image
                          </Button>
                        </div>
                      ) : (
                        <div className="carousel-images-preview" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {(heroContent.hero_images || []).length > 0 ? (
                            (heroContent.hero_images || []).map((img, idx) => (
                              <div key={idx} className="image-preview" style={{ width: '150px' }}>
                                <img src={img} alt={`Carousel ${idx + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '4px' }} />
                              </div>
                            ))
                          ) : (
                            <p style={{ color: '#a0aec0', fontSize: '14px' }}>No carousel images set — using primary image</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>Slide Interval (seconds)</label>
                      {editingHero ? (
                        <Input 
                          type="number"
                          min="3"
                          max="15"
                          value={(heroContent.slide_interval || 6000) / 1000} 
                          onChange={(e) => setHeroContent({...heroContent, slide_interval: parseInt(e.target.value) * 1000})}
                          placeholder="6"
                        />
                      ) : (
                        <p>{(heroContent.slide_interval || 6000) / 1000} seconds</p>
                      )}
                    </div>
                    <div className="field full-width">
                      {editingHero ? (
                        <MediaUpload 
                          label="Hero Video (Optional - for fast loading, keep under 10MB)"
                          value={heroContent.hero_video || ''} 
                          onChange={(url) => setHeroContent({...heroContent, hero_video: url})}
                          token={token}
                          acceptVideo={true}
                        />
                      ) : heroContent.hero_video && (
                        <>
                          <label>Hero Video</label>
                          <div className="video-preview">
                            <video src={heroContent.hero_video} controls muted style={{ maxWidth: '100%', maxHeight: '200px' }} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Philosophy Section */}
              <div className="content-section">
                <div className="section-header">
                  <h3><FileText size={20} /> Philosophy Section</h3>
                  {!editingPhilosophy ? (
                    <Button onClick={() => setEditingPhilosophy(true)} className="edit-btn">
                      <Edit2 size={16} /> Edit
                    </Button>
                  ) : (
                    <div className="edit-actions">
                      <Button onClick={savePhilosophyContent} className="save-btn">
                        <Save size={16} /> Save
                      </Button>
                      <Button onClick={() => setEditingPhilosophy(false)} className="cancel-btn">
                        <X size={16} />
                      </Button>
                    </div>
                  )}
                </div>
                {philosophyContent && (
                  <div className="content-fields">
                    <div className="field">
                      <label>Title</label>
                      {editingPhilosophy ? (
                        <Input 
                          value={philosophyContent.title || ''} 
                          onChange={(e) => setPhilosophyContent({...philosophyContent, title: e.target.value})}
                        />
                      ) : (
                        <p>{philosophyContent.title}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>Content</label>
                      {editingPhilosophy ? (
                        <Textarea 
                          value={philosophyContent.content || ''} 
                          onChange={(e) => setPhilosophyContent({...philosophyContent, content: e.target.value})}
                          rows={4}
                        />
                      ) : (
                        <p>{philosophyContent.content}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Info Section */}
              <div className="content-section">
                <div className="section-header">
                  <h3><Globe size={20} /> Contact Information</h3>
                  {!editingContact ? (
                    <Button onClick={() => setEditingContact(true)} className="edit-btn">
                      <Edit2 size={16} /> Edit
                    </Button>
                  ) : (
                    <div className="edit-actions">
                      <Button onClick={saveContactInfo} className="save-btn">
                        <Save size={16} /> Save
                      </Button>
                      <Button onClick={() => setEditingContact(false)} className="cancel-btn">
                        <X size={16} />
                      </Button>
                    </div>
                  )}
                </div>
                {contactInfo && (
                  <div className="content-fields contact-grid">
                    <div className="field">
                      <label>Email</label>
                      {editingContact ? (
                        <Input 
                          value={contactInfo.email || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                        />
                      ) : (
                        <p>{contactInfo.email}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>Phone</label>
                      {editingContact ? (
                        <Input 
                          value={contactInfo.phone || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                        />
                      ) : (
                        <p>{contactInfo.phone}</p>
                      )}
                    </div>
                    <div className="field full-width">
                      <label>Address</label>
                      {editingContact ? (
                        <Textarea 
                          value={contactInfo.address || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})}
                          rows={2}
                        />
                      ) : (
                        <p>{contactInfo.address}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>Instagram URL</label>
                      {editingContact ? (
                        <Input 
                          value={contactInfo.instagram || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, instagram: e.target.value})}
                        />
                      ) : (
                        <p>{contactInfo.instagram}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>LinkedIn URL</label>
                      {editingContact ? (
                        <Input 
                          value={contactInfo.linkedin || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, linkedin: e.target.value})}
                        />
                      ) : (
                        <p>{contactInfo.linkedin}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>WhatsApp Number</label>
                      {editingContact ? (
                        <Input 
                          value={contactInfo.whatsapp || ''} 
                          onChange={(e) => setContactInfo({...contactInfo, whatsapp: e.target.value})}
                        />
                      ) : (
                        <p>{contactInfo.whatsapp}</p>
                      )}
                    </div>
                    <div className="field full-width" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                      <h4 style={{ margin: '0 0 12px', color: '#4a5568', fontSize: '14px', fontWeight: 600 }}>Additional Social / External Links</h4>
                    </div>
                    {[1, 2, 3, 4].map(num => (
                      <div className="field" key={`social-${num}`}>
                        <label>Link {num} Label</label>
                        {editingContact ? (
                          <Input 
                            placeholder={`e.g. Facebook, YouTube`}
                            value={contactInfo[`social_link_${num}_label`] || ''} 
                            onChange={(e) => setContactInfo({...contactInfo, [`social_link_${num}_label`]: e.target.value})}
                          />
                        ) : (
                          <p>{contactInfo[`social_link_${num}_label`] || '-'}</p>
                        )}
                        <label style={{ marginTop: '4px' }}>URL</label>
                        {editingContact ? (
                          <Input 
                            placeholder="https://..."
                            value={contactInfo[`social_link_${num}`] || ''} 
                            onChange={(e) => setContactInfo({...contactInfo, [`social_link_${num}`]: e.target.value})}
                          />
                        ) : (
                          <p>{contactInfo[`social_link_${num}`] || '-'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            {/* Homepage Settings Section */}
            <div className="content-section homepage-settings-section">
              <div className="content-card">
                <div className="section-header">
                  <div>
                    <h3><Home size={20} /> Homepage Settings</h3>
                    <p className="card-description" style={{margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#666'}}>Manage "Why JNT" and CTA sections</p>
                  </div>
                  <Button 
                    onClick={() => setEditingHomepageSettings(!editingHomepageSettings)} 
                    className={editingHomepageSettings ? 'cancel-btn' : 'edit-btn'}
                  >
                    {editingHomepageSettings ? <><X size={16} /> Cancel</> : <><Edit2 size={16} /> Edit</>}
                  </Button>
                </div>
                {homepageSettings && (
                  <div className="form-grid">
                    <div className="field full-width">
                      <h4 style={{marginBottom: '1rem', color: 'var(--muted-gold)'}}>Why JNT Section</h4>
                    </div>
                    <div className="field">
                      <label>Title</label>
                      {editingHomepageSettings ? (
                        <Input 
                          value={homepageSettings.why_jnt_title || ''} 
                          onChange={(e) => setHomepageSettings({...homepageSettings, why_jnt_title: e.target.value})}
                        />
                      ) : (
                        <p>{homepageSettings.why_jnt_title}</p>
                      )}
                    </div>
                    <div className="field">
                      <label>Subtitle</label>
                      {editingHomepageSettings ? (
                        <Input 
                          value={homepageSettings.why_jnt_subtitle || ''} 
                          onChange={(e) => setHomepageSettings({...homepageSettings, why_jnt_subtitle: e.target.value})}
                        />
                      ) : (
                        <p>{homepageSettings.why_jnt_subtitle}</p>
                      )}
                    </div>
                    <div className="field full-width">
                      <label>Reason Cards</label>
                      {editingHomepageSettings ? (
                        <div className="reason-cards-editor">
                          {(homepageSettings.why_jnt_reasons || []).map((reason, idx) => (
                            <div key={idx} style={{background: '#f9f9f9', padding: '1rem', borderRadius: '8px', marginBottom: '0.5rem'}}>
                              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0.5rem'}}>
                                <Input placeholder="Icon" value={reason.icon || ''} onChange={(e) => {
                                  const updated = [...homepageSettings.why_jnt_reasons];
                                  updated[idx] = {...updated[idx], icon: e.target.value};
                                  setHomepageSettings({...homepageSettings, why_jnt_reasons: updated});
                                }} />
                                <Input placeholder="Title" value={reason.title || ''} onChange={(e) => {
                                  const updated = [...homepageSettings.why_jnt_reasons];
                                  updated[idx] = {...updated[idx], title: e.target.value};
                                  setHomepageSettings({...homepageSettings, why_jnt_reasons: updated});
                                }} />
                                <Input placeholder="Description" value={reason.description || ''} onChange={(e) => {
                                  const updated = [...homepageSettings.why_jnt_reasons];
                                  updated[idx] = {...updated[idx], description: e.target.value};
                                  setHomepageSettings({...homepageSettings, why_jnt_reasons: updated});
                                }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          {(homepageSettings.why_jnt_reasons || []).map((reason, idx) => (
                            <div key={idx} style={{background: '#f9f9f9', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.5rem'}}>
                              <strong>{reason.icon}</strong> | {reason.title}: {reason.description}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="field full-width">
                      <h4 style={{marginTop: '1.5rem', marginBottom: '1rem', color: 'var(--muted-gold)'}}>CTA Section</h4>
                    </div>
                    <div className="field">
                      <label>CTA Title</label>
                      {editingHomepageSettings ? (
                        <Input value={homepageSettings.cta_title || ''} onChange={(e) => setHomepageSettings({...homepageSettings, cta_title: e.target.value})} />
                      ) : (<p>{homepageSettings.cta_title}</p>)}
                    </div>
                    <div className="field">
                      <label>CTA Subtitle</label>
                      {editingHomepageSettings ? (
                        <Input value={homepageSettings.cta_subtitle || ''} onChange={(e) => setHomepageSettings({...homepageSettings, cta_subtitle: e.target.value})} />
                      ) : (<p>{homepageSettings.cta_subtitle}</p>)}
                    </div>
                    <div className="field">
                      <label>Primary Button</label>
                      {editingHomepageSettings ? (
                        <Input value={homepageSettings.cta_primary_text || ''} onChange={(e) => setHomepageSettings({...homepageSettings, cta_primary_text: e.target.value})} />
                      ) : (<p>{homepageSettings.cta_primary_text}</p>)}
                    </div>
                    <div className="field">
                      <label>Secondary Button</label>
                      {editingHomepageSettings ? (
                        <Input value={homepageSettings.cta_secondary_text || ''} onChange={(e) => setHomepageSettings({...homepageSettings, cta_secondary_text: e.target.value})} />
                      ) : (<p>{homepageSettings.cta_secondary_text}</p>)}
                    </div>
                    {editingHomepageSettings && (
                      <div className="field full-width">
                        <Button onClick={saveHomepageSettings} className="save-btn"><Save size={16} /> Save</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <SettingsPanel token={token} seedData={seedData} currentUser={currentUser} />
          )}

          {/* Design Journey Tab */}
          {activeTab === 'design-journey' && designJourneyContent && (
            <div className="design-journey-content">
              <div className="content-section">
                <div className="section-header">
                  <h3>Design My Journey Page Settings</h3>
                  <Button 
                    onClick={() => setEditingDesignJourney(!editingDesignJourney)}
                    className={editingDesignJourney ? 'cancel-btn' : 'edit-btn'}
                  >
                    {editingDesignJourney ? <><X size={16} /> Cancel</> : <><Edit2 size={16} /> Edit</>}
                  </Button>
                </div>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label>Hero Title</label>
                    <Input
                      value={designJourneyContent.hero_title || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, hero_title: e.target.value})}
                      disabled={!editingDesignJourney}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Hero Tagline</label>
                    <Input
                      value={designJourneyContent.hero_tagline || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, hero_tagline: e.target.value})}
                      disabled={!editingDesignJourney}
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label>Hero Hook Text</label>
                    <Textarea
                      value={designJourneyContent.hero_hook || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, hero_hook: e.target.value})}
                      disabled={!editingDesignJourney}
                      rows={2}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Value Section Heading</label>
                    <Input
                      value={designJourneyContent.value_heading || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, value_heading: e.target.value})}
                      disabled={!editingDesignJourney}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Value Section Subheading</label>
                    <Input
                      value={designJourneyContent.value_subheading || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, value_subheading: e.target.value})}
                      disabled={!editingDesignJourney}
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label>Trust Quote</label>
                    <Textarea
                      value={designJourneyContent.trust_quote || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, trust_quote: e.target.value})}
                      disabled={!editingDesignJourney}
                      rows={2}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Trust Badge Text</label>
                    <Input
                      value={designJourneyContent.trust_badge_text || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, trust_badge_text: e.target.value})}
                      disabled={!editingDesignJourney}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Form Title</label>
                    <Input
                      value={designJourneyContent.form_title || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, form_title: e.target.value})}
                      disabled={!editingDesignJourney}
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label>Form Subtitle</label>
                    <Textarea
                      value={designJourneyContent.form_subtitle || ''}
                      onChange={(e) => setDesignJourneyContent({...designJourneyContent, form_subtitle: e.target.value})}
                      disabled={!editingDesignJourney}
                      rows={2}
                    />
                  </div>
                </div>
                
                {/* Value Cards Editor */}
                <div className="value-cards-section">
                  <h4>Feature Cards (6 cards shown on page)</h4>
                  <div className="value-cards-grid">
                    {(designJourneyContent.value_cards || []).map((card, index) => (
                      <div key={index} className="value-card-editor">
                        <div className="card-header">
                          <span className="card-number">Card {index + 1}</span>
                          <span className="card-icon">{card.icon}</span>
                        </div>
                        <Input
                          placeholder="Title"
                          value={card.title || ''}
                          onChange={(e) => {
                            const newCards = [...designJourneyContent.value_cards];
                            newCards[index] = {...newCards[index], title: e.target.value};
                            setDesignJourneyContent({...designJourneyContent, value_cards: newCards});
                          }}
                          disabled={!editingDesignJourney}
                          className="mb-2"
                        />
                        <Textarea
                          placeholder="Description"
                          value={card.description || ''}
                          onChange={(e) => {
                            const newCards = [...designJourneyContent.value_cards];
                            newCards[index] = {...newCards[index], description: e.target.value};
                            setDesignJourneyContent({...designJourneyContent, value_cards: newCards});
                          }}
                          disabled={!editingDesignJourney}
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                {editingDesignJourney && (
                  <div className="save-actions">
                    <Button 
                      onClick={async () => {
                        try {
                          const headers = { Authorization: `Bearer ${token}` };
                          await axios.put(`${API}/admin/content/design-journey`, designJourneyContent, { headers });
                          setEditingDesignJourney(false);
                          alert('Design Journey settings saved successfully');
                        } catch (error) {
                          console.error('Error saving:', error);
                          alert('Failed to save settings');
                        }
                      }}
                      className="save-btn"
                    >
                      <Save size={16} />
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>

              {/* Itinerary Form Editor */}
              <AdminErrorBoundary name="Form Editor">
              <FormFieldEditor token={token} />
              </AdminErrorBoundary>
            </div>
          )}

          {/* My Profile Tab */}
          {activeTab === 'my-profile' && (
            <AdminErrorBoundary name="My Profile">
            <AdminProfilePanel token={token} currentUser={currentUser} />
            </AdminErrorBoundary>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
